"use client";

import Image from "next/image";
import { MapPin, X } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import type { Reference, ReferenceCategory } from "@/lib/content/references";

type ReferencesExplorerProps = {
  items: Reference[];
  categories: Array<"Vše" | ReferenceCategory>;
};

export function ReferencesExplorer({ items, categories }: ReferencesExplorerProps) {
  const [active, setActive] = useState<"Vše" | ReferenceCategory>("Vše");
  const [selected, setSelected] = useState<Reference | null>(null);

  const filtered = active === "Vše" ? items : items.filter((item) => item.category === active);

  useEffect(() => {
    if (!selected) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [selected]);

  return (
    <div>
      <div role="group" aria-label="Filtr referencí" className="flex flex-wrap gap-2">
        {categories.map((category) => {
          const isActive = category === active;
          return (
            <button
              key={category}
              type="button"
              aria-pressed={isActive}
              onClick={() => setActive(category)}
              className={cn(
                "rounded-full px-4 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-green text-white shadow-[0_8px_20px_-10px_rgba(76,135,0,0.9)]"
                  : "bg-white text-ink-soft ring-1 ring-line hover:text-blue-dark hover:ring-blue/30",
              )}
            >
              {category}
            </button>
          );
        })}
      </div>

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((item) => (
          <article
            key={item.slug}
            className="group flex flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-card transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1"
          >
            <button
              type="button"
              onClick={() => setSelected(item)}
              className="relative block aspect-[3/2] w-full overflow-hidden bg-surface-alt"
              aria-label={`Zvětšit fotografii: ${item.title}`}
            >
              <Image
                src={item.image}
                alt={item.title}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                className="object-cover transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
              />
              <span className="absolute top-3 left-3 rounded-full bg-night/75 px-3 py-1 text-[11px] font-medium tracking-wide text-white backdrop-blur-sm">
                {item.category}
              </span>
            </button>

            <div className="flex flex-1 flex-col p-5">
              <h3 className="text-base leading-snug font-semibold text-blue-dark">
                {item.title}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="size-3.5" aria-hidden="true" />
                  {item.location}
                </span>
                {item.routes ? (
                  <span className="rounded-full bg-green-soft px-2 py-0.5 font-medium text-green-dark">
                    {item.routes}
                  </span>
                ) : null}
              </div>
              <p className="mt-3 text-sm leading-relaxed text-ink-soft">
                {item.description}
              </p>
            </div>
          </article>
        ))}
      </div>

      {selected ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-night/85 p-4 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={selected.title}
            className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-white/12 bg-night-soft shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              autoFocus
              onClick={() => setSelected(null)}
              aria-label="Zavřít náhled"
              className="absolute top-3 right-3 z-10 inline-flex size-9 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
            >
              <X className="size-4" />
            </button>
            <div className="relative aspect-[3/2] w-full bg-black/40">
              <Image
                src={selected.image}
                alt={selected.title}
                fill
                sizes="(max-width: 768px) 100vw, 768px"
                className="object-contain"
              />
            </div>
            <div className="p-5 sm:p-6">
              <h3 className="text-lg font-semibold text-white">{selected.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/70">
                {selected.description}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/80">
                  {selected.category}
                </span>
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/80">
                  {selected.location}
                </span>
                {selected.routes ? (
                  <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/80">
                    {selected.routes}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
